import type { CustomFieldEntity, CustomFieldValue } from '@pm/schemas';
import { formatPhone } from '@pm/schemas';
import { Button, Checkbox, Input, LabelCaps, Select, Skeleton, Textarea } from '@pm/ui';
import { Check, Pencil, X } from 'lucide-react';
import { useState } from 'react';
import { UserPicker } from '@/components/pickers';
import { api, ApiRequestError } from '@/lib/api';
import { fmtDate, fmtDateTime, fmtMoney, str } from '@/lib/format';
import { mgKeys, useClients, useCustomFieldValues, useInvalidating } from '@/lib/queries';
import { useToast } from '@/lib/toast';

/** SPEC §38 — campos customizados de TASK ou CLIENT: leitura compacta, edição em lote. */
export function CustomFieldsPanel({
  entity,
  id,
  canEdit,
  compact,
}: {
  entity: CustomFieldEntity;
  id: string;
  canEdit: boolean;
  compact?: boolean;
}) {
  const toast = useToast();
  const { data, isLoading } = useCustomFieldValues(entity, id);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const path = entity === 'TASK' ? 'tasks' : 'clients';
  const save = useInvalidating(
    (values: Record<string, unknown>) =>
      api.put<CustomFieldValue[]>(`/${path}/${id}/custom-fields`, { values }),
    [mgKeys.values(entity, id)],
  );
  if (isLoading) return <Skeleton className="w-2/3" />;
  if (!data || data.length === 0) return null;

  const startEdit = () => {
    setDraft(Object.fromEntries(data.map((v) => [v.definition.key, v.value])));
    setErrors({});
    setEditing(true);
  };
  const submit = () =>
    save.mutate(draft, {
      onSuccess: () => (toast.success('Campos salvos'), setEditing(false)),
      onError: (e) => {
        if (e instanceof ApiRequestError && e.code === 'CUSTOM_FIELD_INVALID') {
          const fields =
            (e.details?.fields as { key: string; message: string }[] | undefined) ?? [];
          setErrors(Object.fromEntries(fields.map((f) => [f.key, f.message])));
        } else toast.error(e);
      },
    });

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <LabelCaps>Campos personalizados</LabelCaps>
        {canEdit ? (
          editing ? (
            <span className="ml-auto flex gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                className="size-6"
                aria-label="Cancelar"
                onClick={() => setEditing(false)}
              >
                <X />
              </Button>
              <Button
                variant="primary"
                size="icon-sm"
                className="size-6"
                aria-label="Salvar"
                loading={save.isPending}
                onClick={submit}
              >
                <Check />
              </Button>
            </span>
          ) : (
            <Button
              variant="ghost"
              size="icon-sm"
              className="ml-auto size-6"
              aria-label="Editar campos"
              onClick={startEdit}
            >
              <Pencil />
            </Button>
          )
        ) : null}
      </div>
      {editing ? (
        <div className={compact ? 'flex flex-col gap-2' : 'grid gap-3 sm:grid-cols-2'}>
          {data.map((v) => (
            <FieldEditor
              key={v.definition.id}
              field={v}
              value={draft[v.definition.key]}
              error={errors[v.definition.key]}
              onChange={(val) => setDraft({ ...draft, [v.definition.key]: val })}
            />
          ))}
        </div>
      ) : (
        <dl
          className={
            compact
              ? 'grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs'
              : 'grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm'
          }
        >
          {data.map((v) => (
            <FieldRow key={v.definition.id} field={v} />
          ))}
        </dl>
      )}
    </div>
  );
}

function FieldRow({ field }: { field: CustomFieldValue }) {
  const { definition: d, value, display } = field;
  let text: React.ReactNode = <span className="text-text-3">—</span>;
  if (value !== null && value !== undefined && value !== '') {
    switch (d.field_type) {
      case 'BOOLEAN':
        text = value ? 'Sim' : 'Não';
        break;
      case 'CURRENCY':
        text = <span className="font-mono">{fmtMoney(value as number)}</span>;
        break;
      case 'NUMBER':
        text = <span className="font-mono">{str(value)}</span>;
        break;
      case 'DATE':
        text = <span className="font-mono">{fmtDate(`${str(value)}T12:00:00`)}</span>;
        break;
      case 'DATETIME':
        text = <span className="font-mono">{fmtDateTime(str(value))}</span>;
        break;
      case 'URL':
        text = (
          <a
            href={str(value)}
            target="_blank"
            rel="noreferrer"
            className="text-accent hover:underline"
          >
            {str(value)}
          </a>
        );
        break;
      case 'USER':
      case 'CLIENT':
        text = display ?? str(value);
        break;
      default:
        text = str(value);
    }
  }
  return (
    <>
      <dt className="text-text-3">{d.name}</dt>
      <dd className="min-w-0 truncate">{text}</dd>
    </>
  );
}

function FieldEditor({
  field,
  value,
  error,
  onChange,
}: {
  field: CustomFieldValue;
  value: unknown;
  error?: string;
  onChange: (v: unknown) => void;
}) {
  const d = field.definition;
  const { data: clients } = useClients(
    d.field_type === 'CLIENT' ? { status: 'ACTIVE' } : undefined,
  );
  const text = str(value);
  const label = (
    <span className="text-xs text-text-2">
      {d.name}
      {d.required ? ' *' : ''}
    </span>
  );
  const err = error ? <span className="text-xs text-danger">{error}</span> : null;
  const wrap = (input: React.ReactNode) => (
    <label className="flex flex-col gap-1">
      {label}
      {input}
      {err}
    </label>
  );
  switch (d.field_type) {
    case 'TEXTAREA':
      return wrap(
        <Textarea
          value={text}
          onChange={(e) => onChange(e.target.value)}
          className="min-h-[56px] text-sm"
        />,
      );
    case 'BOOLEAN':
      return (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={value === true} onCheckedChange={(v) => onChange(v === true)} />{' '}
          {d.name}
          {err}
        </label>
      );
    case 'SELECT':
      return wrap(
        <Select
          value={text}
          onChange={(e) => onChange(e.target.value || null)}
          className="h-control-sm text-sm"
        >
          <option value="">—</option>
          {d.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Select>,
      );
    case 'MULTISELECT': {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="flex flex-col gap-1">
          {label}
          <div className="flex flex-wrap gap-1.5">
            {d.options.map((o) => (
              <label
                key={o}
                className="flex items-center gap-1.5 rounded border border-border px-1.5 py-0.5 text-xs"
              >
                <Checkbox
                  checked={arr.includes(o)}
                  onCheckedChange={(v) => onChange(v ? [...arr, o] : arr.filter((x) => x !== o))}
                />
                {o}
              </label>
            ))}
          </div>
          {err}
        </div>
      );
    }
    case 'USER':
      return (
        <div className="flex flex-col gap-1">
          {label}
          <UserPicker
            value={typeof value === 'string' ? value : null}
            onChange={(id) => onChange(id)}
            size="sm"
            placeholder="—"
          />
          {err}
        </div>
      );
    case 'CLIENT':
      return wrap(
        <Select
          value={text}
          onChange={(e) => onChange(e.target.value || null)}
          className="h-control-sm text-sm"
        >
          <option value="">—</option>
          {(clients?.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.trade_name ?? c.legal_name}
            </option>
          ))}
        </Select>,
      );
    case 'NUMBER':
    case 'CURRENCY':
      return wrap(
        <Input
          type="number"
          step={d.field_type === 'CURRENCY' ? '0.01' : 'any'}
          mono
          value={text}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className="h-control-sm text-sm"
        />,
      );
    case 'DATE':
      return wrap(
        <Input
          type="date"
          mono
          value={text}
          onChange={(e) => onChange(e.target.value || null)}
          className="h-control-sm text-sm"
        />,
      );
    case 'DATETIME':
      return wrap(
        <Input
          type="datetime-local"
          mono
          value={text ? text.slice(0, 16) : ''}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
          className="h-control-sm text-sm"
        />,
      );
    default:
      return wrap(
        <Input
          type={d.field_type === 'EMAIL' ? 'email' : d.field_type === 'URL' ? 'url' : 'text'}
          value={text}
          onChange={(e) =>
            onChange(d.field_type === 'PHONE' ? formatPhone(e.target.value) : e.target.value)
          }
          mono={d.field_type === 'PHONE' || d.field_type === 'URL'}
          className="h-control-sm text-sm"
        />,
      );
  }
}
